import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { getUserType } from '../../utils/userPermissions'
import NextStepGuide from '../../components/enterprise/NextStepGuide'
import WorkflowProgress from '../../components/enterprise/WorkflowProgress'
import { 
  ShoppingCart, 
  FileText, 
  Truck, 
  CreditCard, 
  Star, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react'
import { toast } from 'react-toastify'

interface DashboardStats {
  purchaseOrders: {
    total: number
    pending: number
    accepted: number
    completed: number
    cancelled: number
  }
  agreements: {
    total: number
    pending: number
    signed: number
    expired: number
  }
  fulfillments: {
    total: number
    pending: number
    shipped: number
    delivered: number
    issueReported: number
  }
  invoices: {
    counts: {
      total: number
      pending: number
      paid: number
      overdue: number
      cancelled: number
    }
    amounts: {
      total: number
      pending: number
      paid: number
      overdue: number
    }
  }
}

const PostQuoteActivityPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      console.log('PostQuoteActivityPage: No user found, redirecting to login')
      navigate('/auth/login')
      return
    }

    // Check if user is enterprise user
    const userType = getUserType(user)
    const isEnterpriseUser = userType === 'ENTERPRISE_BUYER' || userType === 'ENTERPRISE_VENDOR'
    
    console.log('PostQuoteActivityPage access check:')
    console.log('- User:', user)
    console.log('- User type:', userType)
    console.log('- Is enterprise user:', isEnterpriseUser)
    console.log('- User role:', user.role)
    console.log('- User isEnterprise:', user.isEnterprise)
    
    if (!isEnterpriseUser) {
      console.log('PostQuoteActivityPage: User is not enterprise, redirecting to home')
      navigate('/', { replace: true })
      return
    }

    console.log('PostQuoteActivityPage: Access granted, loading stats')
    loadDashboardStats()
  }, [user, navigate])

  const loadDashboardStats = async () => {
    try {
      setLoading(true)
      
      const [poStats, agreementStats, fulfillmentStats, invoiceStats] = await Promise.all([
        apiService.get('/api/post-quote-activity/purchase-orders/statistics'),
        apiService.get('/api/post-quote-activity/digital-agreements/statistics'),
        apiService.get('/api/post-quote-activity/fulfillments/statistics'),
                  apiService.get('/api/post-quote-activity/invoices/statistics')
      ]) as any[]

      console.log('API Response Debug:')
      console.log('- poStats:', poStats)
      console.log('- agreementStats:', agreementStats)
      console.log('- fulfillmentStats:', fulfillmentStats)
      console.log('- invoiceStats:', invoiceStats)

      // Safely access the data with fallbacks
      const safePoStats = poStats?.data || { total: 0, pending: 0, accepted: 0, completed: 0, cancelled: 0 }
      const safeAgreementStats = agreementStats?.data || { total: 0, pending: 0, signed: 0, expired: 0 }
      const safeFulfillmentStats = fulfillmentStats?.data || { total: 0, pending: 0, shipped: 0, delivered: 0, issueReported: 0 }
      const safeInvoiceStats = invoiceStats?.data || { 
        counts: { total: 0, pending: 0, paid: 0, overdue: 0, cancelled: 0 },
        amounts: { total: 0, pending: 0, paid: 0, overdue: 0 }
      }

      console.log('Safe stats:')
      console.log('- safePoStats:', safePoStats)
      console.log('- safeAgreementStats:', safeAgreementStats)
      console.log('- safeFulfillmentStats:', safeFulfillmentStats)
      console.log('- safeInvoiceStats:', safeInvoiceStats)

      setStats({
        purchaseOrders: safePoStats,
        agreements: safeAgreementStats,
        fulfillments: safeFulfillmentStats,
        invoices: safeInvoiceStats
      })
    } catch (error) {
      console.error('Error loading dashboard stats:', error)
      console.error('Error details:', error)
      toast.error('Failed to load dashboard statistics')
      
      // Set fallback stats on error
      setStats({
        purchaseOrders: { total: 0, pending: 0, accepted: 0, completed: 0, cancelled: 0 },
        agreements: { total: 0, pending: 0, signed: 0, expired: 0 },
        fulfillments: { total: 0, pending: 0, shipped: 0, delivered: 0, issueReported: 0 },
        invoices: { 
          counts: { total: 0, pending: 0, paid: 0, overdue: 0, cancelled: 0 },
          amounts: { total: 0, pending: 0, paid: 0, overdue: 0 }
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const StatCard: React.FC<{
    title: string
    value: number
    icon: React.ReactNode
    color: string
    onClick?: () => void
  }> = ({ title, value, icon, color, onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg ${
        onClick ? 'hover:scale-105' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  )

  const ModuleCard: React.FC<{
    title: string
    description: string
    icon: React.ReactNode
    color: string
    onClick: () => void
  }> = ({ title, description, icon, color, onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${color}`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4">
        <div className="p-3 bg-blue-100 rounded-full">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Post-Quote Activity Manager
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage purchase orders, contracts, deliveries, invoices, and feedback
          </p>
        </div>

        {/* Workflow Progress */}
        <WorkflowProgress userRole={user?.role as 'BUYER' | 'VENDOR'} />

        {/* Next Step Guide */}
        <NextStepGuide userRole={user?.role as 'BUYER' | 'VENDOR'} />

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Purchase Orders"
              value={stats.purchaseOrders.total}
              icon={<ShoppingCart className="w-6 h-6 text-blue-600" />}
              color="bg-blue-50"
              onClick={() => {
                console.log('Purchase Orders button clicked')
                console.log('Navigating to:', '/enterprise/post-quote-activity/purchase-orders')
                navigate('/enterprise/post-quote-activity/purchase-orders')
              }}
            />
            <StatCard
              title="Digital Agreements"
              value={stats.agreements.total}
              icon={<FileText className="w-6 h-6 text-green-600" />}
              color="bg-green-50"
              onClick={() => navigate('/enterprise/post-quote-activity/agreements')}
            />
            <StatCard
              title="Fulfillments"
              value={stats.fulfillments.total}
              icon={<Truck className="w-6 h-6 text-orange-600" />}
              color="bg-orange-50"
              onClick={() => navigate('/enterprise/post-quote-activity/fulfillments')}
            />
            <StatCard
              title="Invoices"
              value={stats.invoices.counts.total}
              icon={<CreditCard className="w-6 h-6 text-purple-600" />}
              color="bg-purple-50"
              onClick={() => navigate('/enterprise/post-quote-activity/invoices')}
            />
          </div>
        )}

        {/* Quick Status Overview */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Purchase Orders Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ShoppingCart className="w-5 h-5 text-blue-600 mr-2" />
                Purchase Orders Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="flex items-center text-sm font-medium">
                    <Clock className="w-4 h-4 text-yellow-500 mr-1" />
                    {stats.purchaseOrders.pending}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Accepted</span>
                  <span className="flex items-center text-sm font-medium">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                    {stats.purchaseOrders.accepted}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="flex items-center text-sm font-medium">
                    <TrendingUp className="w-4 h-4 text-blue-500 mr-1" />
                    {stats.purchaseOrders.completed}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cancelled</span>
                  <span className="flex items-center text-sm font-medium">
                    <XCircle className="w-4 h-4 text-red-500 mr-1" />
                    {stats.purchaseOrders.cancelled}
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 text-purple-600 mr-2" />
                Financial Overview
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Invoiced</span>
                  <span className="text-sm font-medium text-green-600">
                    ${stats.invoices.amounts.total.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending Payment</span>
                  <span className="text-sm font-medium text-yellow-600">
                    ${stats.invoices.amounts.pending.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Paid</span>
                  <span className="text-sm font-medium text-green-600">
                    ${stats.invoices.amounts.paid.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Overdue</span>
                  <span className="text-sm font-medium text-red-600">
                    ${stats.invoices.amounts.overdue.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Module Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ModuleCard
            title="Purchase Orders"
            description="Create and manage purchase orders from accepted RFQ responses"
            icon={<ShoppingCart className="w-8 h-8 text-blue-600" />}
            color="hover:bg-blue-50"
            onClick={() => {
              console.log('Purchase Orders ModuleCard clicked')
              console.log('Navigating to:', '/enterprise/post-quote-activity/purchase-orders')
              navigate('/enterprise/post-quote-activity/purchase-orders')
            }}
          />
          <ModuleCard
            title="Digital Agreements"
            description="Upload and manage contracts, NDAs, and legal documents"
            icon={<FileText className="w-8 h-8 text-green-600" />}
            color="hover:bg-green-50"
            onClick={() => navigate('/enterprise/post-quote-activity/agreements')}
          />
          <ModuleCard
            title="Fulfillment Tracking"
            description="Track delivery status and manage shipping information"
            icon={<Truck className="w-8 h-8 text-orange-600" />}
            color="hover:bg-orange-50"
            onClick={() => navigate('/enterprise/post-quote-activity/fulfillments')}
          />
          <ModuleCard
            title="Invoicing"
            description="Generate invoices and track payment status"
            icon={<CreditCard className="w-8 h-8 text-purple-600" />}
            color="hover:bg-purple-50"
            onClick={() => navigate('/enterprise/post-quote-activity/invoices')}
          />
          <ModuleCard
            title="Vendor Feedback"
            description="Rate vendors and leave reviews after order completion"
            icon={<Star className="w-8 h-8 text-yellow-600" />}
            color="hover:bg-yellow-50"
            onClick={() => navigate('/enterprise/post-quote-activity/feedback')}
          />
          <ModuleCard
            title="Reports & Analytics"
            description="View comprehensive reports and performance analytics"
            icon={<TrendingUp className="w-8 h-8 text-indigo-600" />}
            color="hover:bg-indigo-50"
            onClick={() => navigate('/enterprise/post-quote-activity/reports')}
          />
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="text-center text-gray-500 py-8">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Recent activity will appear here as you use the system</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PostQuoteActivityPage 